// Extracted from original/src/App.tsx (ADR-08 §2.1):
//   page frame + <aside> sidebar  :25290-25716 (logo, office name, username, USD ticker, nav, backup/logout, support footer, collapse button)
//   <main> frame                  :25718-25754 (the backup banner :25721-25752 is replaced by the preview banner, D-223 / D-152)
//   <header>                      :25755-26290 (title, date line, support / theme / bell + dropdown / search / dashboard action buttons)
// Cut: the sidebar key list and permission filter (:25388-25443; the container decides the items, D-124 / D-129),
// per-tab search resets, notification click routing (:25882-26010), fetch calls behind the refresh button, the
// "calculator & chat" and "add debt" header variants of other tabs (:26232-26288, B4 → `headerExtra` slot).
import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Activity, AlertTriangle, ArrowLeftRight, Bell, Calculator, CheckCircle, Database, Edit2, LogOut, Menu,
  MessageSquare, Moon, RefreshCw, Search, ShieldCheck, Sun, User, X, Zap,
} from 'lucide-react';
import { Logo } from '../components/HelperComponents.js';
import { PreviewBanner, type PreviewBannerData } from '../components/PreviewBanner.js';
import { SidebarItem } from '../components/SidebarItem.js';
import { containerVariants } from '../constants.js';
import { useI18n } from '../i18n-context.js';

export interface SidebarNavItem {
  readonly key: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly active: boolean;
  readonly onClick: () => void;
}

export interface HeaderNotification {
  readonly id: string;
  readonly title: string;
  readonly message: string;
  /** Already formatted by the container. */
  readonly date: string;
  readonly kind: 'stagnant_debt' | 'transaction' | 'other';
}

export interface LayoutProps {
  readonly officeName: string;
  readonly username: string;
  readonly logoUrl?: string | null;
  /** Formatted strings (App.tsx:25356-25377); `null` hides the ticker (D-066 `usd_dealing_hidden`). */
  readonly rate?: { readonly buy: string; readonly sell: string; readonly price: string } | null;
  readonly onRateClick?: () => void;
  readonly items: readonly SidebarNavItem[];
  readonly collapsed: boolean;
  readonly onToggleCollapsed: () => void;
  readonly themeColor: string;
  readonly onBackup?: () => void;
  readonly onLogout: () => void;
  readonly onSupport?: () => void;
  readonly banner?: PreviewBannerData | null;
  readonly pageTitle: string;
  /** Formatted date line (App.tsx:25776-25782: en-US MM-DD-YYYY). */
  readonly dateLine: string;
  readonly darkMode: boolean;
  readonly onToggleTheme: () => void;
  readonly notifications: readonly HeaderNotification[];
  readonly notificationsEnabled: boolean;
  readonly onNotificationClick?: (id: string) => void;
  readonly onDismissNotification?: (id: string) => void;
  readonly onClearNotifications?: () => void;
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  /** Dashboard header actions (App.tsx:26106-26230). */
  readonly dashboardActions?: {
    readonly onRefresh: () => void;
    readonly isEditing: boolean;
    readonly onStartEditing: () => void;
    readonly onDoneEditing: () => void;
    readonly onCalculator: () => void;
    readonly onWatchdog: () => void;
    readonly onChat: () => void;
    readonly unreadChatCount: number;
  } | null;
  /** Header actions of other tabs (B4). */
  readonly headerExtra?: ReactNode;
  readonly children: ReactNode;
}

export function Layout(props: LayoutProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const isSidebarOpen = !props.collapsed;
  const [showNotifications, setShowNotifications] = useState(false);
  const notifications = props.notifications;
  const notificationsEnabled = props.notificationsEnabled;
  const themeColor = props.themeColor;
  const darkMode = props.darkMode;
  const da = props.dashboardActions;

  return (
    <>
      <div
        className={`flex h-screen bg-[#d6e2e2] dark:bg-[#0d1317] text-gray-900 dark:text-[#f0f6fc] font-sans ${lang === "ar" ? "font-arabic" : ""}`}
        dir={lang === "ar" ? "rtl" : "ltr"}
      >
        {/* Sidebar */}
        <motion.aside
          initial={{ x: lang === "ar" ? 300 : -300 }}
          animate={{ x: 0, width: isSidebarOpen ? 256 : 80 }}
          data-testid="sidebar"
          className={`
h-screen md:h-[calc(100vh-1.25rem)] sticky top-0 md:top-2.5 md:my-2.5 md:ms-2.5 md:rounded-[30px]
bg-[#121619]/95 dark:bg-[#0b0f12]/95 backdrop-blur-2xl text-white transition-all duration-300 flex flex-col items-center pt-5 pb-1 relative shadow-2xl border border-white/10 dark:border-white/5 z-50
`}
        >
          <div
            // nosemgrep: useless-ternary -- copied verbatim from the old App.tsx (ADR-08 §2.1)
            className={`mb-1 px-3 text-center flex flex-col items-center transition-all duration-300 ${isSidebarOpen ? "gap-0.5" : "gap-0.5"} group relative`}
          >
            <motion.div
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.8, ease: "anticipate" }}
              className="relative z-10"
            >
              <Logo size={isSidebarOpen ? 32 : 18} {...(props.logoUrl !== undefined ? { logoUrl: props.logoUrl } : {})} />
            </motion.div>
            <div className="flex flex-col items-center relative z-10">
              <h1
                className={`font-black tracking-tight transition-all duration-300 ${isSidebarOpen ? "text-sm" : "text-[8px] uppercase"} dark:digital-glow`}
              >
                {isSidebarOpen ? props.officeName : props.officeName.split(" ")[0]}
              </h1>
              {isSidebarOpen && (
                <motion.span
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-[9px] text-amber-400 font-bold uppercase tracking-widest mt-0.5 flex items-center gap-1"
                >
                  <User size={8} />
                  {props.username}
                </motion.span>
              )}
            </div>
          </div>
          {/* USD Rate Widget (Manual) */}
          {props.rate && (
            <div className="mb-1 px-2.5 w-full transition-all duration-300">
              <button
                onClick={props.onRateClick}
                data-testid="sidebar-rate"
                className={`w-full bg-white/5 dark:bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 dark:border-white/5 transition-all duration-300 hover:bg-white/10 dark:hover:bg-white/10 py-1 px-1.5 flex flex-col items-center justify-center ${!isSidebarOpen && "p-1"}`}
              >
                {isSidebarOpen ? (
                  <div className="flex items-center justify-between gap-2 w-full text-center">
                    <div className="flex flex-col text-left">
                      <span className="text-[7px] text-gray-400 font-bold uppercase">
                        {t('buyRate')}
                      </span>
                      <span className="text-[10px] font-black text-white font-mono">
                        {props.rate.buy}
                      </span>
                    </div>
                    <span className="text-[8px] font-black text-amber-400 uppercase tracking-wider bg-amber-400/10 px-1 py-0.5 rounded">
                      {t('usdRate')}
                    </span>
                    <div className="flex flex-col text-right">
                      <span className="text-[7px] text-gray-400 font-bold uppercase">
                        {t('sellRate')}
                      </span>
                      <span className="text-[10px] font-black text-white font-mono">
                        {props.rate.sell}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[9px] font-black text-amber-400">
                      $
                    </span>
                    <span className="text-[8px] font-black text-white font-mono">
                      {props.rate.price}
                    </span>
                  </div>
                )}
              </button>
            </div>
          )}
          <motion.nav
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex-1 w-full px-2 flex flex-col gap-0.5 overflow-y-auto custom-scrollbar py-1"
          >
            <AnimatePresence mode="popLayout">
              {props.items.map((item) => (
                <motion.div
                  key={item.key}
                  layout
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  data-testid={`nav-${item.key}`}
                >
                  <SidebarItem
                    icon={item.icon}
                    label={item.label}
                    active={item.active}
                    onClick={item.onClick}
                    collapsed={!isSidebarOpen}
                    themeColor={themeColor}
                    lang={lang}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.nav>
          <div className="mt-auto px-2 pb-1.5 w-full border-t border-white/5 pt-1.5 flex flex-col gap-1">
            <div
              className={`flex ${isSidebarOpen ? "flex-row" : "flex-col"} gap-1 w-full`}
            >
              <button
                onClick={props.onBackup}
                className={`flex items-center justify-center gap-1 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-600 py-1 rounded-lg transition-all group cursor-pointer ${isSidebarOpen ? "flex-1 text-[10px] font-bold" : "p-1.5"}`}
                title={t('layout.backup_title')}
              >
                <Database
                  size={12}
                  className="group-hover:scale-110 transition-transform shrink-0"
                />
                {isSidebarOpen && (
                  <span className="truncate">
                    {t('layout.backup')}
                  </span>
                )}
              </button>
              <button
                onClick={props.onLogout}
                data-testid="logout"
                className={`flex items-center justify-center gap-1 bg-rose-600/10 hover:bg-rose-600/20 text-rose-600 py-1 rounded-lg transition-all group cursor-pointer ${isSidebarOpen ? "flex-1 text-[10px] font-bold" : "p-1.5"}`}
                title={t('layout.logout')}
              >
                <LogOut
                  size={12}
                  className="group-hover:translate-x-0.5 transition-transform shrink-0"
                />
                {isSidebarOpen && (
                  <span className="truncate">
                    {t('layout.logout')}
                  </span>
                )}
              </button>
            </div>
            {/* Compact support & copyright info footer */}
            {isSidebarOpen ? (
              <div className="text-center py-0.5 mt-0.5 border-t border-white/5 flex flex-col gap-0.5 items-center">
                <span className="text-[8px] text-gray-500 font-bold uppercase tracking-wider">
                  © {t('layout.copyright')}
                </span>
                <button
                  onClick={props.onSupport}
                  className="text-[9px] text-amber-500 hover:text-amber-400 font-extrabold transition-colors flex items-center gap-1 cursor-pointer"
                >
                  <ShieldCheck size={9} className="shrink-0" />
                  <span>
                    {t('layout.support')}
                  </span>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center border-t border-white/5 pt-2 mt-1">
                <button
                  onClick={props.onSupport}
                  className="p-1.5 bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                  title={t('layout.support_title')}
                >
                  <ShieldCheck size={14} />
                </button>
              </div>
            )}
          </div>
          <button
            onClick={props.onToggleCollapsed}
            data-testid="sidebar-toggle"
            className={`absolute ${lang === "ar" ? "-left-3" : "-right-3"} top-1/2 transform -translate-y-1/2 bg-[#1e272e] p-1 rounded-full border border-white/10 text-white/50 hover:text-white`}
          >
            {isSidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </motion.aside>
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-0 bg-transparent transition-colors duration-300">
          {props.banner && <PreviewBanner banner={props.banner} />}
          <div className="p-4 md:p-8 max-w-[1600px] mx-auto w-full">
            <header className="flex justify-between items-center mb-8">
              <div>
                <h2 data-testid="page-title" className="text-3xl font-black text-gray-900 dark:text-[#f0f6fc] tracking-tight dark:digital-glow">
                  {props.pageTitle}
                </h2>
                <p className="text-gray-500 dark:text-[#8b949e] mt-1 font-bold uppercase tracking-widest text-xs">
                  {props.dateLine}
                </p>
              </div>
              <div className="flex gap-3 items-center">
                {/* Direct Support & Ownership Fixed Icon Button */}
                <button
                  id="header_support_btn"
                  onClick={props.onSupport}
                  className="p-2.5 bg-white/90 dark:bg-[#12191f]/90 backdrop-blur-xl border border-white/90 dark:border-white/10 rounded-2xl text-amber-500 dark:text-amber-400 hover:border-[#f97342]/40 transition-all shadow-xs active:scale-95 group cursor-pointer relative"
                  title={t('layout.support_title')}
                >
                  <ShieldCheck
                    size={19}
                    className="filter drop-shadow-[0_0_3px_rgba(245,158,11,0.2)] group-hover:scale-110 transition-transform duration-300"
                  />
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full border border-white dark:border-[#0d1117] animate-ping" />
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full border border-white dark:border-[#0d1117]" />
                </button>
                <button
                  onClick={props.onToggleTheme}
                  data-testid="theme-toggle"
                  className="p-2.5 bg-white/90 dark:bg-[#12191f]/90 backdrop-blur-xl border border-white/90 dark:border-white/10 rounded-2xl text-gray-600 dark:text-[#8b949e] hover:bg-white dark:hover:bg-[#18222b] hover:border-[#f97342]/40 transition-all shadow-xs"
                  title={darkMode ? t('lightMode') : t('darkMode')}
                >
                  {darkMode ? (
                    <Sun
                      size={19}
                      className="text-amber-400 filter drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]"
                    />
                  ) : (
                    <Moon
                      size={19}
                      className="text-slate-700 filter drop-shadow-[0_0_8px_rgba(15,23,42,0.3)]"
                    />
                  )}
                </button>
                <div className="relative notification-container">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    data-testid="notifications-toggle"
                    className="p-2.5 bg-white/90 dark:bg-[#12191f]/90 backdrop-blur-xl border border-white/90 dark:border-white/10 rounded-2xl text-gray-600 dark:text-[#8b949e] hover:bg-white dark:hover:bg-[#18222b] hover:border-[#f97342]/40 transition-all shadow-xs relative"
                    title={t('layout.notifications')}
                  >
                    <Bell size={19} />
                    {notifications.length > 0 && notificationsEnabled && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#f97342] text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white dark:border-[#0d1117] shadow-sm animate-pulse">
                        {notifications.length}
                      </span>
                    )}
                  </button>
                  <AnimatePresence>
                    {showNotifications && (
                      <>
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className={`absolute top-full mt-2 ${lang === "ar" ? "right-0" : "left-0"} w-80 bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-2xl shadow-2xl z-50 overflow-hidden dark:digital-border`}
                        >
                          <div className="p-4 border-b border-gray-50 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#05070a]">
                            <h3 className="text-sm font-black uppercase tracking-widest dark:text-white">
                              {t('layout.notifications')}
                            </h3>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {t('layout.alerts_count', { n: notifications.length })}
                              </span>
                              {notifications.length > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    props.onClearNotifications?.();
                                  }}
                                  className="text-[10px] font-black text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 hover:underline cursor-pointer uppercase tracking-widest transition-all"
                                >
                                  {t('layout.clear_all')}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="max-h-96 overflow-y-auto custom-scrollbar">
                            {!notificationsEnabled ? (
                              <div className="p-12 text-center flex flex-col items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-[#161b22] flex items-center justify-center text-gray-300 dark:text-gray-700">
                                  <Bell size={24} />
                                </div>
                                <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest">
                                  {t('layout.notifications_disabled')}
                                </p>
                              </div>
                            ) : notifications.length > 0 ? (
                              notifications.map((n) => (
                                <div
                                  key={n.id}
                                  className="p-4 border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22] transition-colors cursor-pointer group relative"
                                  onClick={() => {
                                    props.onNotificationClick?.(n.id);
                                    setShowNotifications(false);
                                  }}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      props.onDismissNotification?.(n.id);
                                    }}
                                    className={`absolute top-4 ${lang === "ar" ? "left-4" : "right-4"} p-1 bg-gray-50 dark:bg-[#161b22] hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg text-gray-400 hover:text-rose-500 transition-all border border-gray-100 dark:border-[#30363d] shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100`}
                                    title={t('common.close')}
                                  >
                                    <X size={10} />
                                  </button>
                                  <div className="flex gap-3">
                                    <div
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                        n.kind === "stagnant_debt"
                                          ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
                                          : n.kind === "transaction"
                                            ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                                            : "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                                      }`}
                                    >
                                      {n.kind === "stagnant_debt" ? (
                                        <AlertTriangle size={16} />
                                      ) : n.kind === "transaction" ? (
                                        <ArrowLeftRight size={16} />
                                      ) : (
                                        <Zap size={16} />
                                      )}
                                    </div>
                                    <div
                                      className={`flex flex-col gap-1 ${lang === "ar" ? "pl-6" : "pr-6"}`}
                                    >
                                      <p className="text-xs font-black dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                        {n.title}
                                      </p>
                                      <p className="text-[10px] text-gray-500 dark:text-[#8b949e] font-bold leading-relaxed">
                                        {n.message}
                                      </p>
                                      <span className="text-[9px] text-gray-400 font-mono mt-1">
                                        {n.date}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="p-12 text-center flex flex-col items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-[#161b22] flex items-center justify-center text-gray-300 dark:text-gray-700">
                                  <Bell size={24} />
                                </div>
                                <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest">
                                  {t('layout.no_notifications')}
                                </p>
                              </div>
                            )}
                          </div>
                          {notifications.length > 0 && (
                            <div className="p-3 bg-gray-50 dark:bg-[#05070a] border-t border-gray-100 dark:border-[#30363d] text-center">
                              <button
                                onClick={() => setShowNotifications(false)}
                                className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest hover:underline"
                              >
                                {t('common.close')}
                              </button>
                            </div>
                          )}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                <div className="relative">
                  <Search
                    className={`absolute ${lang === "ar" ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#8b949e]`}
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder={t('search')}
                    value={props.searchValue}
                    onChange={(e) => props.onSearchChange(e.target.value)}
                    data-testid="header-search"
                    className={`bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl ${lang === "ar" ? "pr-10 pl-10" : "pl-10 pr-10"} py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-64 dark:text-[#f0f6fc] dark:digital-border transition-all`}
                  />
                  {props.searchValue && (
                    <button
                      onClick={() => props.onSearchChange("")}
                      className={`absolute ${lang === "ar" ? "left-3" : "right-3"} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-rose-500 transition-all`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                {da && (
                  <button
                    onClick={da.onRefresh}
                    data-testid="header-refresh"
                    className="p-2 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl text-gray-500 dark:text-[#8b949e] hover:bg-gray-50 dark:hover:bg-[#161b22] transition-all shadow-sm dark:digital-border group"
                    title={t('layout.refresh')}
                  >
                    <RefreshCw
                      size={20}
                      className="group-active:rotate-180 transition-transform duration-500"
                    />
                  </button>
                )}
                {da && (
                  <div className="flex gap-2">
                    {da.isEditing ? (
                      <button
                        onClick={da.onDoneEditing}
                        className="px-6 py-2 bg-[#f97342] hover:bg-[#ea580c] text-white rounded-2xl font-black text-sm shadow-lg shadow-orange-500/20 transition-all active:scale-95 flex items-center gap-2"
                      >
                        <CheckCircle size={18} />
                        {t('layout.done')}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={da.onStartEditing}
                          className="p-2.5 bg-white/90 dark:bg-[#12191f]/90 backdrop-blur-xl border border-white/90 dark:border-white/10 rounded-2xl text-blue-500 dark:text-blue-400 hover:bg-white dark:hover:bg-[#18222b] hover:border-[#f97342]/40 transition-all shadow-xs group"
                          title={t('editDashboard')}
                        >
                          <Edit2
                            size={18}
                            className="group-hover:scale-110 transition-transform"
                          />
                        </button>
                        <button
                          type="button"
                          id="header_quick_calculator_btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            da.onCalculator();
                          }}
                          className="p-2.5 bg-white/90 dark:bg-[#12191f]/90 backdrop-blur-xl border border-white/90 dark:border-white/10 rounded-2xl text-amber-500 dark:text-amber-400 hover:bg-white dark:hover:bg-[#18222b] hover:border-[#f97342]/40 transition-all shadow-xs group cursor-pointer active:scale-95"
                          title={t('layout.calculator')}
                        >
                          <Calculator
                            size={18}
                            className="group-hover:scale-110 transition-transform text-amber-500 dark:text-amber-400"
                          />
                        </button>
                        <button
                          onClick={da.onWatchdog}
                          className="p-2.5 bg-white/90 dark:bg-[#12191f]/90 backdrop-blur-xl border border-white/90 dark:border-white/10 rounded-2xl text-rose-500 dark:text-rose-400 hover:bg-white dark:hover:bg-[#18222b] hover:border-[#f97342]/40 transition-all shadow-xs group relative"
                          title={t('layout.watchdog')}
                        >
                          <Activity
                            size={18}
                            className="group-hover:scale-110 transition-transform"
                          />
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full animate-ping opacity-75" />
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white dark:border-[#0d1117]" />
                        </button>
                        <button
                          type="button"
                          onClick={da.onChat}
                          className="p-2.5 bg-white/90 dark:bg-[#12191f]/90 backdrop-blur-xl border border-white/90 dark:border-white/10 rounded-2xl text-indigo-600 dark:text-indigo-400 hover:bg-white dark:hover:bg-[#18222b] hover:border-[#f97342]/40 transition-all shadow-xs group cursor-pointer active:scale-95 relative"
                          title={t('layout.chat')}
                        >
                          <MessageSquare
                            size={18}
                            className="group-hover:scale-110 transition-transform text-indigo-600 dark:text-indigo-400"
                          />
                          {da.unreadChatCount > 0 ? (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-rose-500 text-white text-[10px] font-black rounded-full flex items-center justify-center px-1 shadow-md animate-bounce border-2 border-white dark:border-[#0d1117]">
                              {da.unreadChatCount}
                            </span>
                          ) : (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#f97342] rounded-full animate-pulse border-2 border-white dark:border-[#0d1117]" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {props.headerExtra}
              </div>
            </header>
            {props.children}
          </div>
        </main>
      </div>
    </>
  );
}
